import { useEffect } from 'react';
import { useAchievementAlerts } from '../state/achievementAlerts';
import { useFeedback } from '../state/feedback';
import { usePlayTime } from '../state/playTime';
import { useShell } from '../state/shell';

/** Whether anything other than the bare shell is on screen: a sheet or dialog, another hunter's profile, or an achievement alert waiting. */
function somethingElseIsOpen(): boolean {
  if (document.querySelector('[role="dialog"]')) return true;
  if (useShell.getState().profile != null) return true;
  return useAchievementAlerts.getState().pending.length > 0;
}

/**
 * Counts play time once a second while the page is visible, and opens the feedback panel when a
 * milestone is due and nothing else is on screen. Mount it only while the game shell is shown,
 * which is signed in with a usable location.
 */
export function useFeedbackPrompt() {
  useEffect(() => {
    const time = usePlayTime.getState();
    let timer: ReturnType<typeof setInterval> | null = null;

    const onTick = () => {
      time.tick();
      if (useFeedback.getState().isOpen || somethingElseIsOpen()) return;
      const due = time.takeDueMilestone();
      if (due != null) useFeedback.getState().open();
    };
    const start = () => {
      if (timer != null) return;
      time.resume();
      timer = setInterval(onTick, 1000);
    };
    const stop = () => {
      if (timer != null) clearInterval(timer);
      timer = null;
      time.pause();
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);
}
